import { z } from "zod";
import { emailStatusSchema, pipelineStatusSchema } from "../enums.js";
import {
  isoDateTimeSchema,
  nonEmptyStringSchema,
  uuidSchema,
} from "../primitives.js";

const nullableUrlSchema = z.union([z.string().url(), z.null()]);

export const hotelSchema = z.object({
  id: uuidSchema,
  name: nonEmptyStringSchema,
  website: nullableUrlSchema,
  domain: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  stars: z.number().nullable(),
  room_count: z.number().int().nullable(),
  has_shuttle: z.boolean().nullable(),
  has_airport_transfer: z.boolean().nullable(),
  pipeline_status: pipelineStatusSchema,
  owner_user_id: uuidSchema.nullable(),
  lead_score: z.number().int().min(0).max(100).nullable(),
  priority: z.number().int().nullable(),
  next_action: z.string().nullable(),
  next_action_due_at: isoDateTimeSchema.nullable(),
  scoring_ruleset_version: z.string().nullable(),
  scoring_rationale: z.string().nullable(),
  do_not_contact: z.boolean(),
  last_researched_at: isoDateTimeSchema.nullable(),
  created_at: isoDateTimeSchema,
  updated_at: isoDateTimeSchema,
});

export type Hotel = z.infer<typeof hotelSchema>;

/**
 * Lean list DTO for GET /v1/hotels (WP26).
 * Omits scoring rationale, website/address internals, and ownership fields.
 */
export const hotelListPrimaryContactSchema = z
  .object({
    id: uuidSchema,
    name: nonEmptyStringSchema,
    email_status: emailStatusSchema,
    is_primary: z.boolean(),
  })
  .nullable();

export const hotelListItemSchema = z.object({
  id: uuidSchema,
  name: nonEmptyStringSchema,
  city: z.string().nullable(),
  country: z.string().nullable(),
  pipeline_status: pipelineStatusSchema,
  lead_score: z.number().int().min(0).max(100).nullable(),
  priority: z.number().int().nullable(),
  has_shuttle: z.boolean().nullable(),
  has_airport_transfer: z.boolean().nullable(),
  last_researched_at: isoDateTimeSchema.nullable(),
  next_action: z.string().nullable(),
  next_action_due_at: isoDateTimeSchema.nullable(),
  primary_contact: hotelListPrimaryContactSchema,
});

export type HotelListItem = z.infer<typeof hotelListItemSchema>;

export const hotelListResponseSchema = z.object({
  items: z.array(hotelListItemSchema),
  next_cursor: z.string().nullable(),
});

export type HotelListResponse = z.infer<typeof hotelListResponseSchema>;

/** Draft row summary for lead detail (no body text). */
export const hotelDraftSummarySchema = z.object({
  id: uuidSchema,
  purpose: z.string(),
  subject: nonEmptyStringSchema,
  status: z.string(),
  contact_id: uuidSchema.nullable(),
  updated_at: isoDateTimeSchema,
});

export type HotelDraftSummary = z.infer<typeof hotelDraftSummarySchema>;

/** Contact summary on lead detail. */
export const hotelDetailContactSchema = z.object({
  id: uuidSchema,
  name: nonEmptyStringSchema,
  role_title: z.string().nullable(),
  email: z.union([z.string().email(), z.null()]),
  email_status: emailStatusSchema,
  is_primary: z.boolean(),
  confidence: z.number().min(0).max(1).nullable(),
});

export type HotelDetailContact = z.infer<typeof hotelDetailContactSchema>;

/** Claim with source for lead detail. */
export const hotelDetailClaimSchema = z.object({
  id: uuidSchema,
  field: nonEmptyStringSchema,
  value_json: z.unknown(),
  confidence: z.number().min(0).max(1),
  /** Source URL string (DB may predate strict URL validation). */
  source_url: nonEmptyStringSchema,
  source_artifact_id: uuidSchema.nullable(),
  observed_at: isoDateTimeSchema,
});

export type HotelDetailClaim = z.infer<typeof hotelDetailClaimSchema>;

export const hotelDetailSchema = z.object({
  hotel: hotelSchema,
  claims: z.array(hotelDetailClaimSchema),
  contacts: z.array(hotelDetailContactSchema),
  drafts: z.array(hotelDraftSummarySchema),
});

export type HotelDetail = z.infer<typeof hotelDetailSchema>;
